/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { getEmbeddedResourcesAsArray } from '@common/tech-shared';
import { BinaryFileListLinkRel } from './binary-file.linkrel';
import { BinaryFileListResource, BinaryFileResource, ScanStatus } from './binary-file.model';

export function isScanPending(binaryFile: BinaryFileResource): boolean {
  return hasFileScanStatus(binaryFile, ScanStatus.PENDING);
}

export function isFileClean(binaryFile: BinaryFileResource): boolean {
  return hasFileScanStatus(binaryFile, ScanStatus.CLEAN);
}

export function isFileInfected(binaryFile: BinaryFileResource): boolean {
  return hasFileScanStatus(binaryFile, ScanStatus.INFECTED);
}

function hasFileScanStatus(binaryFile: BinaryFileResource, status: ScanStatus): boolean {
  return binaryFile.lastScanResult?.scanStatus === status;
}

export interface ArchiveDownloadState {
  tooltip: string;
  disabled: boolean;
}

export const ARCHIVE_DOWNLOAD_TOOLTIP_ALL_CLEAN = 'Anhänge als Zip-Datei herunterladen';
export const ARCHIVE_DOWNLOAD_TOOLTIP_ALL_INFECTED = 'Infizierte Anhänge können nicht heruntergeladen werden';
export const ARCHIVE_DOWNLOAD_TOOLTIP_PENDING = 'Herunterladen erst möglich, wenn der Virenscan abgeschlossen ist';
export const ARCHIVE_DOWNLOAD_TOOLTIP_MIXED = 'Nicht infizierte Anhänge als Zip-Datei herunterladen';

export function getArchiveDownloadState(binaryFileListResource: BinaryFileListResource): ArchiveDownloadState {
  const files: BinaryFileResource[] =
    getEmbeddedResourcesAsArray<BinaryFileResource>(binaryFileListResource, BinaryFileListLinkRel.FILE_LIST) ?? [];
  const hasPending: boolean = files.some((file) => isScanPending(file));
  const hasClean: boolean = files.some((file) => isFileClean(file));
  const hasInfected: boolean = files.some((file) => isFileInfected(file));

  if (hasPending) {
    return { tooltip: ARCHIVE_DOWNLOAD_TOOLTIP_PENDING, disabled: true };
  }
  if (hasInfected) {
    if (hasClean) {
      return { tooltip: ARCHIVE_DOWNLOAD_TOOLTIP_MIXED, disabled: false };
    } else {
      return { tooltip: ARCHIVE_DOWNLOAD_TOOLTIP_ALL_INFECTED, disabled: true };
    }
  }
  return { tooltip: ARCHIVE_DOWNLOAD_TOOLTIP_ALL_CLEAN, disabled: false };
}
